'use client';

import React from 'react';
import { Box, Typography } from '@mui/material';
import Image from 'next/image';
import Link from 'next/link';

interface LogoProps {
  size?: 'small' | 'medium' | 'large' | 'xlarge';
  showText?: boolean;
  href?: string;
  variant?: 'default' | 'footer';
}

const sizeMap = {
  small: { width: 32, height: 32, fontSize: '1.125rem' },
  medium: { width: 44, height: 44, fontSize: '1.5rem' },
  large: { width: 60, height: 60, fontSize: '2rem' },
  xlarge: { width: 80, height: 80, fontSize: '2.5rem' },
};

export function Logo({
  size = 'medium',
  showText = true,
  href = '/',
  variant = 'default'
}: LogoProps) {
  const dimensions = sizeMap[size];

  const logoContent = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 1.5, sm: 2 },
        cursor: href ? 'pointer' : 'default',
        transition: 'transform 0.2s ease',
        '&:hover': href ? {
          transform: 'scale(1.02)',
        } : {},
      }}
    >
      {/* Logo SVG */}
      <Box
        sx={{
          width: dimensions.width,
          height: dimensions.height,
          position: 'relative',
          flexShrink: 0,
          filter: 'drop-shadow(0 2px 8px rgba(0, 0, 0, 0.1))',
        }}
      >
        <Image
          src="/me-letter-ethiopian-colors.svg"
          alt="Mengir Logo"
          width={dimensions.width}
          height={dimensions.height}
          priority
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
          }}
        />
      </Box>

      {/* Brand Name */}
      {showText && (
        <Typography
          variant="h6"
          sx={{
            fontSize: dimensions.fontSize,
            fontWeight: 800,
            letterSpacing: '-0.02em',
            background: variant === 'footer'
              ? 'linear-gradient(135deg, #22c55e, #fbbf24, #ef4444)'
              : 'linear-gradient(135deg, #8b5cf6, #22c55e)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            userSelect: 'none',
          }}
        >
          Mengir መንግር
        </Typography>
      )}
    </Box>
  );

  if (href) {
    return (
      <Link href={href} style={{ textDecoration: 'none' }}>
        {logoContent}
      </Link>
    );
  }

  return logoContent;
}
